import { useSnackbar } from 'contexts/snackbar';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import { ASSIGNMENT_PERSISTENCE_INTENT } from 'utils/assignmentPersistencePolicy';
import { compareCanvasRubrics, hasD2lTextOnlyRubricMismatch } from 'utils/helper';

const useAssignmentLmsReconciliation = ({
    auth,
    form,
    persistedAssignment,
    setDialog,
    closeDialog,
    setPushRubricDialog,
}) => {
    const { t } = useTranslation('assignment');
    const { message } = useSnackbar();

    return useCallback(
        async ({ intent, onContinue, onCancel }) => {
            if (!form.api_assignment_id) return onContinue();
            if (process.env.REACT_APP_KIND === 'D2L' && hasD2lTextOnlyRubricMismatch(form)) {
                setDialog({
                    open: true,
                    message: t('dialog.d2l-textonly-rubric.title', 'Update this rubric in D2L'),
                    description: t(
                        'dialog.d2l-textonly-rubric.description',
                        'Update the rubric in D2L, then resync it.'
                    ),
                    variant: 'primary',
                    actionButtonText: t('common:controls.ok', 'OK'),
                    cancelButtonText: '',
                    onAction: () => {
                        onCancel?.();
                        closeDialog();
                    },
                    onCancel: () => {
                        onCancel?.();
                        closeDialog();
                    },
                    onClose: () => {
                        onCancel?.();
                        closeDialog();
                    },
                });
                return { status: 'cancelled' };
            }
            if (process.env.REACT_APP_KIND !== 'CANVAS') return onContinue();
            let remote;
            try {
                remote = await apiService.getAssignment({
                    course_id: auth.course_id,
                    accessToken: localStorage.getItem('access_token'),
                    assignment_id: form.api_assignment_id,
                });
            } catch (error) {
                onCancel?.();
                message.error(t('message.load-error', 'We could not load the LMS assignment. Please try again.'));
                return { status: 'failed' };
            }
            if (
                compareCanvasRubrics(form, remote.rubric, remote.rubric_settings) ||
                (intent === ASSIGNMENT_PERSISTENCE_INTENT.UNPUBLISH && !form.rubric)
            ) {
                return onContinue();
            }
            setPushRubricDialog({
                open: true,
                form: { ...(persistedAssignment || {}), ...form },
                onSave: ({ api_data, rubric }) => onContinue({ apiData: api_data, rubric }),
            });
            return { status: 'cancelled' };
        },
        [auth.course_id, closeDialog, form, message, persistedAssignment, setDialog, setPushRubricDialog, t]
    );
};

export default useAssignmentLmsReconciliation;
